import { useEffect, useState } from "react";
import {
  Modal,
  BlockStack,
  InlineStack,
  Text,
  Banner,
  Box,
  Checkbox,
  Spinner,
  Icon,
} from "@shopify/polaris";
import { CheckCircleIcon, AlertCircleIcon } from "@shopify/polaris-icons";
import supabase from "../../supabase-client";
import { formatPrice } from "../../utility/funcs";
import { previewPayable, authorizePayouts, waitForPayout, messageFor } from "../../utility/pagos";

// Returning a deposit by SPEI, automatically, through Fintoc.
//
// Only offered when the deposit itself ARRIVED through Fintoc (a `payments` row
// applied to this reservation as purpose 'deposit'): that is the only case where
// we know, from the bank, which account the money came from.
//
// The destination is ALWAYS the account the deposit came from. It is not a
// choice, and that is the whole safety model: the account comes from Fintoc's
// record of the incoming transfer, so no one — staff included — can redirect
// the money, and no account verification is needed before paying. It also
// makes the books trivial: every automatic return pairs 1:1 with the deposit
// that funded it.
//
// Consequence: when a third party paid (a relative, an employer), the money
// goes back to them. The UI states this as fact rather than asking anyone to
// approve it — there is no other account to choose, so wording that implies a
// choice would only invite confusion.
//
// The checkbox stays, but it means "I told the client how this works", not
// "I got permission".

// Live: pagos-svc owns the money movement. It re-derives the amount and the
// destination from Fintoc's record of the deposit, so nothing the browser sends
// can change where the money goes or how much.

const money = (c) => formatPrice(c / 100);
const fmtClabe = (c) => String(c || "").replace(/(\d{4})(?=\d)/g, "$1 ").trim();
const last4 = (c) => (c ? `···${String(c).slice(-4)}` : "");

// Deposit received through Fintoc for this reservation, net of reversals, and
// the payment it came from (the largest one, when there is more than one).
export async function loadFintocDepositSource(reservationId) {
  const { data: apps } = await supabase
    .from("payment_applications")
    .select(
      "amount_cents, payment:payments(id, rail, source, occurred_at, counterparty_name, counterparty_rfc, counterparty_account, counterparty_bank)"
    )
    .eq("reservation_id", reservationId)
    .eq("purpose", "deposit");

  const byPayment = {};
  for (const a of apps || []) {
    const p = a.payment;
    if (!p || p.source !== "fintoc" || p.rail !== "spei") continue;
    byPayment[p.id] = byPayment[p.id] || { payment: p, cents: 0 };
    byPayment[p.id].cents += Number(a.amount_cents);
  }
  const list = Object.values(byPayment).filter((x) => x.cents > 0);
  if (!list.length) return null;
  list.sort((a, b) => b.cents - a.cents);
  return { cents: list.reduce((s, x) => s + x.cents, 0), payment: list[0].payment };
}

// Loose "is this the same person" check, for the warning only — never a gate.
function sameHolder(a, b) {
  const toks = (s) =>
    new Set(
      String(s || "")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toUpperCase()
        .split(/[^A-Z0-9]+/)
        .filter((t) => t.length > 2 && !["SA", "CV", "DE", "LOS", "LAS", "DEL"].includes(t))
    );
  const x = toks(a);
  const y = toks(b);
  if (!x.size || !y.size) return false;
  let hit = 0;
  for (const t of x) if (y.has(t)) hit += 1;
  return hit >= Math.min(2, Math.min(x.size, y.size));
}

// The one possible destination: where the deposit came from.
export function originDestination(source) {
  const p = source?.payment;
  if (!p?.counterparty_account) return null;
  return {
    id: "source",
    clabe: p.counterparty_account,
    holder: p.counterparty_name,
    bank: p.counterparty_bank,
    rfc: p.counterparty_rfc,
  };
}

// Shown inside the Devolver Depósito modal once "SPEI automático" is chosen.
// Not a chooser — there is only one possible destination.
export function FintocDestination({ destination, clientName }) {
  if (!destination) {
    return (
      <Banner tone="warning">
        No se conoce la cuenta de origen de este depósito, así que no se puede devolver
        automáticamente. Usa una transferencia manual.
      </Banner>
    );
  }
  const third = destination.holder && clientName && !sameHolder(destination.holder, clientName);
  return (
    <BlockStack gap="200">
      <Text variant="headingSm" as="h3">Se devuelve a la cuenta de origen</Text>
      <Box borderWidth="025" borderColor="border" borderRadius="200" padding="300">
        <BlockStack gap="100">
          <Text as="p" fontWeight="semibold">{destination.holder || "Titular no reportado"}</Text>
          <Text as="p" tone="subdued" variant="bodySm">
            {[destination.bank, last4(destination.clabe)].filter(Boolean).join(" · ")}
          </Text>
        </BlockStack>
      </Box>
      <Text tone="subdued" variant="bodySm">
        El depósito regresa a la cuenta desde la que se pagó.
      </Text>
      {third && (
        <Text tone="subdued" variant="bodySm">
          Ese depósito lo envió {destination.holder}, así que ahí regresa.
        </Text>
      )}
    </BlockStack>
  );
}

const STEPS = [
  "Enviando transferencia…",
  "Confirmando con el banco…",
  "¡Listo!",
  "Pendiente de fondeo",
  "Sigue en proceso",
];

// The double check. Shown after the Devolver Depósito modal closes.
// `kind` "card_deposit_return" is the same double check for a deposit charged
// through Clip, going back to the payer's bank-verified CLABE instead of the
// account it came from.
export function ConfirmDepositReturnModal({
  open, amountCents, destination, clientName, reservationId, onClose, onDone,
  kind = "deposit_return",
}) {
  const card = kind === "card_deposit_return";
  const [confirmed, setConfirmed] = useState(false);
  const [step, setStep] = useState(-1); // -1 = not sent yet
  const [error, setError] = useState(null);
  // What the SERVER says it would send. The figure on screen is never one the
  // browser worked out for itself — pagos-svc re-derives amount and destination
  // from Fintoc's record of the deposit.
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    if (!open) return undefined;
    setConfirmed(false);
    setStep(-1);
    setError(null);
    setPreview(null);
    let cancelled = false;
    (async () => {
      try {
        const p = await previewPayable(kind, String(reservationId));
        if (!cancelled) {
          setPreview(p);
          if (p.blocked) setError(messageFor(p.blocked.code, p.blocked.message));
        }
      } catch (err) {
        if (!cancelled) setError(err.message || "No se pudo consultar la devolución.");
      }
    })();
    return () => { cancelled = true; };
  }, [open, reservationId, kind]);

  if (!destination) return null;
  const third = destination.holder && clientName && !sameHolder(destination.holder, clientName);
  const serverCents = preview?.amount_cents ?? null;
  const shown = serverCents ?? amountCents;
  // If the server would send somewhere else, something changed underneath us.
  const destMismatch =
    preview && preview.clabe_last4 && String(destination.clabe).slice(-4) !== preview.clabe_last4;
  const ready = !!preview && !preview.blocked && !destMismatch && serverCents > 0;
  const done = step >= 2;   // 2 listo · 3 pendiente de fondeo · 4 en proceso

  const send = async () => {
    setError(null);
    setStep(0);
    try {
      const { results } = await authorizePayouts([
        { payable_kind: kind, payable_id: String(reservationId) },
      ]);
      const r = results?.[0];
      if (!r?.ok) {
        setStep(-1);
        return setError(messageFor(r?.error, r?.message));
      }
      setStep(1);
      const final = await waitForPayout(r.payout_id, (status) => {
        if (status === "submitted") setStep(1);
      });
      if (final.status === "succeeded") return setStep(2);
      if (final.status === "awaiting_funds") return setStep(3);
      if (final.status === "pending_timeout") return setStep(4);
      setStep(-1);
      setError(messageFor(final.error, "La devolución no se completó. Revisa antes de reintentar."));
    } catch (err) {
      setStep(-1);
      setError(err.message || "No se pudo enviar la devolución.");
    }
  };

  return (
    <Modal
      open={open}
      // Mid-send it cannot be dismissed; once done, closing finishes the flow.
      onClose={step >= 0 && !done ? () => {} : done ? () => onDone?.() : onClose}
      title={step < 0 ? "Confirma la devolución" : "Devolución de depósito"}
      primaryAction={
        step < 0
          ? {
              content: `Enviar ${money(shown)}`,
              onAction: send,
              disabled: !confirmed || !ready,
              destructive: true,
            }
          : done
          ? { content: "Cerrar", onAction: () => onDone?.() }
          : undefined
      }
      secondaryActions={step < 0 ? [{ content: "Regresar", onAction: onClose }] : []}
    >
      <Modal.Section>
        {step < 0 ? (
          <BlockStack gap="400">
            {error && <Banner tone="critical">{error}</Banner>}
            {destMismatch && (
              <Banner tone="critical">
                La cuenta de origen cambió. Cierra y vuelve a abrir la devolución.
              </Banner>
            )}
            {preview?.funds && preview.funds.sufficient === false && (
              <Banner tone="warning" title="Saldo insuficiente en Depósitos en garantía">
                Se autoriza ahora y se envía sola en cuanto se fondee la cuenta.
              </Banner>
            )}

            <BlockStack gap="100" inlineAlign="center">
              <Text tone="subdued">Se devolverá</Text>
              <Text variant="heading2xl" as="p" numeric>
                {preview ? money(shown) : "…"}
              </Text>
              <Text tone="subdued" variant="bodySm">Depósito de la reservación #{reservationId}</Text>
            </BlockStack>

            <Box background="bg-surface-secondary" borderRadius="200" padding="400">
              <BlockStack gap="300">
                <Row label="Titular" value={destination.holder || "No reportado"} />
                <Row label="Banco" value={destination.bank || "—"} />
                <Row label="CLABE" value={fmtClabe(destination.clabe)} mono />
                <Row
                  label={card ? "Cuenta" : "Origen"}
                  value={card ? "Verificada por el banco" : "Cuenta con la que pagó el depósito"}
                />
              </BlockStack>
            </Box>

            {third && !card && (
              <Banner tone="info">
                Ese depósito lo envió <b>{destination.holder}</b>, no {clientName}. El dinero regresa
                a esa cuenta.
              </Banner>
            )}

            <Checkbox
              label={card
                ? "Confirmé con el cliente que el depósito regresa a esta cuenta"
                : "Le expliqué al cliente que el depósito regresa a la cuenta desde la que pagó"}
              checked={confirmed}
              onChange={setConfirmed}
            />
          </BlockStack>
        ) : (
          <BlockStack gap="400" inlineAlign="center">
            {step === 2 ? (
              <Icon source={CheckCircleIcon} tone="success" />
            ) : done ? (
              <Icon source={AlertCircleIcon} tone="caution" />
            ) : (
              <Spinner size="large" />
            )}
            <Text variant="headingMd" as="p">{STEPS[step]}</Text>
            <Text tone="subdued" numeric>
              {money(shown)} a {destination.holder || "la cuenta"} {last4(destination.clabe)}
            </Text>
            {step === 3 && (
              <Banner tone="warning">
                La cuenta de devoluciones no tiene saldo suficiente todavía. La devolución quedó
                autorizada y se envía sola en cuanto se fondee — no hay que volver a capturarla.
              </Banner>
            )}
            {step === 4 && (
              <Banner tone="info">
                El banco aún no confirma. La transferencia sigue en proceso; el depósito se marcará
                como devuelto en cuanto llegue la confirmación. No la vuelvas a enviar.
              </Banner>
            )}
          </BlockStack>
        )}
      </Modal.Section>
    </Modal>
  );
}

function Row({ label, value, mono }) {
  return (
    <InlineStack align="space-between" blockAlign="center" gap="400" wrap={false}>
      <Text tone="subdued">{label}</Text>
      <Text as="span" fontWeight="semibold" numeric={mono}>{value}</Text>
    </InlineStack>
  );
}
